/**
 * PATH: packages/email-hub/src/feature/lib/reconnectMailboxes.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-RECONNECTMAILBOXES-001
 * PURPOSE: Pure selector — given the per-mailbox sync status, return the
 *          mailboxes the BACKEND has flagged as needing reconnection.
 * WHY: BUG-2026-06-10-EMAIL-SILENT-SYNC-DEATH. Kept out of the banner
 *      component so it stays a pure, unit-testable I→O function (and the
 *      banner file only exports a component — react-refresh clean).
 */
import type { EmailMailboxSyncStatus } from '../hooks/useEmails.types'

/** Pure I→O: which mailboxes has the backend marked as needing reconnect? */
export function selectReconnectMailboxes(
  mailboxes: EmailMailboxSyncStatus[] | undefined,
): EmailMailboxSyncStatus[] {
  if (!Array.isArray(mailboxes)) return []
  return mailboxes.filter((m) => {
    if (!m) return false
    // `sync_enabled=false` is a DELIBERATE pause by the user — never nag.
    if (m.sync_enabled === false) return false
    // A mailbox the backend disabled (is_active=false) OR one whose last sync
    // recorded an explicit reconnect_required diagnostic is dead until an
    // operator reconnects it.
    if (m.is_active === false) return true
    // A "tokenless zombie": is_active stayed true but the mailbox holds no
    // usable OAuth credentials, so the sync never ran (no error, null
    // last_sync_at) and the inbox is silently empty. The backend flags this
    // as needs_reconnect — surface it so the user knows to re-authorise.
    // BUG-2026-06-24-EMAIL-TOKENLESS-ZOMBIE.
    if (m.needs_reconnect === true) return true
    const err = (m.last_sync_error || '').toLowerCase()
    return err.startsWith('reconnect_required')
  })
}
